import { CamelCaseKeys, Diagnosis, SessionState, UserRole } from '@uplift/types';
import snakecaseKeys from 'snakecase-keys';
import { restClient } from '../client';
import { Addendum, Appointment, AppointmentRecurring, AppointmentType, CPTCode, PaymentTypeName, Session, SessionFormField, SessionFormFieldError, SessionNotesView, SessionParticipation, SessionResource, SessionServiceType } from '../resources';


type formSubmission = {
  // key with value of form field-id and value with field value
  [Key: string]: number | string | boolean | string[] | null;
};

type TreatmentSelection = {
  treatmentApproachId: string;
  interventionIds: string[];
};

type TreatmentSubmission = {
  approachSelections: TreatmentSelection[];
  isTreatmentReviewed?: boolean;
  homework?: string;
};

export type CreateAddendumInput = {
  content: string;
  submittedBillingUpdate: boolean;
};

export type PatchSessionInput = {
  isInitialSession?: boolean;
  isDiagnosisReviewed?: boolean;
  isFollowUpTreatmentGoalReviewed?: boolean;
  riskAssessmentOpted?: boolean;
  mentalStatusExamOpted?: boolean;
  riskAssessment?: formSubmission;
  mentalStatusExam?: formSubmission;
  presentingProblems?: formSubmission;
  treatment?: TreatmentSubmission;
  initialProgress?: formSubmission;
  followUpProgress?: formSubmission;
  serviceType?: SessionServiceType;
  participation?: SessionParticipation;
  assessments?: { submission_id: string; is_reviewed: boolean }[];
  additionalDetails?: string;
  diagnosis_ids?: string[];
  startTime: string;
  endTime: string;
};

export type SubmitSessionInput = {
  identityVerified?: boolean | null;
};


export const getAllDiagnosis = async () => {
  return restClient.request<{data: Diagnosis[]}>('/api/v1/diagnosis');
};

export const getDiagnosisByIds = async (diagnosisIds: string[]) => {
  return restClient.request<{data: Diagnosis[]}>('/api/v1/diagnosis/by_ids', { method: 'POST', data: { diagnosis_ids: diagnosisIds } });
};

export const getCptCode = async (cptCodeId: string) => {
  return restClient.request<{data: CPTCode}>(`/api/v1/cpt/${cptCodeId}`);
};

export const getAllCptCodes = async () => {
  return restClient.request<{ data: CPTCode[] }>('/api/v1/cpt', { method: 'GET' });
};

export const getCptCodesByIds = async (cptCodeIds: string[]) => {
  return restClient.request<{ data: CPTCode[] }>('/api/v1/cpt/by_ids', { method: 'POST', data: { cpt_code_ids: cptCodeIds } });
};

export const getAppointmentById = async (appointmentId: string) => {
  return restClient.request<{ data: Appointment }>(`/api/v1/appointment/${appointmentId}`);
};

export const getAppointmentCancellationDetailsById = async (appointmentId: string) => {
  return restClient.request<{
    data: {
      id: string;
      cancelledByRole: UserRole;
      cancellationType: AppointmentRecurring;
    };
  }>(`/api/v1/view/appointment/${appointmentId}/cancellation_details`);
};

export const getAppointmentCancelAttributes = async (params: {
  appointmentId: string;
  fromDatetime: string;
  seriesId?: string;
}) => {
  return restClient.request<{
    data: {
      disabled_late_fee_reason: (
      | 'initial_user'
      | 'enough_time_to_session'
      | 'session_is_sponsored'
      | 'medicaid'
      | 'no_payment_method'
      );
    };
  }>('/api/v1/appointment-cancel-attributes', { params: snakecaseKeys(params) });
};

export const getNonMaterializedAppointment = async (seriesId: string, fromDatetime: string) => {
  return restClient.request<{ data: Appointment }>('/api/v1/non_materialized_appointment', {
    params: {
      series_id: seriesId,
      from_datetime: fromDatetime,
    },
  });
};

export const getSessionByAppointmentId = async (appointmentId: string) => {
  return restClient.request<{data: Session}>(`/api/v1/appointment/${appointmentId}/session`);
};

export const getAddendaBySessionId = async (sessionId: string) => {
  return restClient.request<{ data: Addendum[] }>(`/api/v1/session/${sessionId}/addendums`);
};

export const createAddendum = (sessionId: string, data: CreateAddendumInput) => restClient.request<{
  data: Addendum;
}>(`/api/v1/session/${sessionId}/addendum`, {
  method: 'POST',
  data: snakecaseKeys(data),
});

export const getSessionSuggestedCPTCodes = async (appointmentId: string) => {
  return restClient.request<{
    data: {
      code: string;
      reasoning: string;
    }[];
  }>(`/api/v1/session/${appointmentId}/cpt_recommendation`);
};

export const getSessionPaymentOptions = async (sessionId: string) => {
  return restClient.request<{
    data: (
    | { label: string; method: PaymentTypeName.cash }
    | { label: string; method: PaymentTypeName.insurance; insurance_id: string }
    )[];
  }>(`/api/v1/session/${sessionId}/payment-options`);
};

export type PsychiatryNoteSectionKey = (
  | 'history_of_presenting_illness'
  | 'psychiatric_review_of_system'
  | 'psychiatric_history'
  | 'social_history'
  | 'medical_history'
  | 'mental_status_exam'
  | 'risk_assessment'
  | 'physical_review_of_systems'
  | 'psychotherapy_evaluation_crisis_services'
  | 'plan'
  | 'diagnosis'
  | 'treatment_education'
  | 'prescribe_refill'
  | 'additional_information'
);

export type PsychiatryNoteSection = {
  note: string;
  fields: SessionFormField[];
  errors: SessionFormFieldError[];
};

export type NoteInternalAssessment = CamelCaseKeys<{ submission_id: string; is_reviewed: boolean }>;

export type PsychiatryNote = CamelCaseKeys<{
  session_id: string;
  is_initial_session: boolean;
  participation: SessionParticipation;
  service_type: SessionServiceType;
  start_time?: string;
  end_time?: string;
  assessments: {
    additionalInformation?: string;
    internal: NoteInternalAssessment[];
    external: { name: string; result: number }[];
  };
  forms: Record<PsychiatryNoteSectionKey, PsychiatryNoteSection>;
}>;

export const psychiatryNoteFormConfigByKey: Record<keyof PsychiatryNote['forms'], { title: string; order: number }> = {
  historyOfPresentingIllness: {
    title: 'History of Presenting Illness',
    order: 0,
  },
  psychiatricReviewOfSystem: {
    title: 'Psychiatric Review of Systems',
    order: 1,
  },
  psychiatricHistory: {
    title: 'Psychiatric History',
    order: 2,
  },
  socialHistory: {
    title: 'Social History',
    order: 3,
  },
  medicalHistory: {
    title: 'Medical History',
    order: 4,
  },
  mentalStatusExam: {
    title: 'Mental Status Exam',
    order: 5,
  },
  riskAssessment: {
    title: 'Risk Assessment',
    order: 6,
  },
  physicalReviewOfSystems: {
    title: 'Physical Review of System',
    order: 7,
  },
  diagnosis: {
    title: 'Diagnosis',
    order: 8,
  },
  plan: {
    title: 'Plan',
    order: 9,
  },
  psychotherapyEvaluationCrisisServices: {
    title: 'Psychotherapy, Evaluation, Crisis Services',
    order: 10
  },
  treatmentEducation: {
    title: 'Treatment Education',
    order: 11
  },
  prescribeRefill: {
    title: 'Prescribe or Refill Medication',
    order: 12
  },
  additionalInformation: {
    title: 'Additional Information',
    order: 13
  },
};

export const getPsychiatryNoteBySessionId = async (sessionId: string) => {
  return restClient.request<{
    data: PsychiatryNote;
  }>(`/api/v1/session/${sessionId}/psychiatry_note`);
};

export const patchSessionPsychiatryNote = async (
  sessionId: string,
  data: Partial<{
    assessments: {
      additional_information?: string;
      internal: { submission_id: string; is_reviewed: boolean }[];
      external: { name: string; result: number }[];
    };
    start_time: string;
    end_time: string;
    service_type: SessionServiceType;
    participation: SessionParticipation;
    forms: Partial<Record<PsychiatryNoteSectionKey, Record<string, string | boolean | number | string[]>>>;
    primary_cpt_code_id: string;
    addon_cpt_code_ids: string[];
    diagnosis_ids: string[];
  }>
) => {
  return restClient.request<{ data: PsychiatryNote } | { data: null; error: string }>(`/api/v1/session/${sessionId}/psychiatry_note`, { method: 'PATCH', data });
};

export const overrideAppointmentState = async (appointmentId: string, state: SessionState) => {
  return restClient.request(`/api/v1/appointment/${appointmentId}/override_state`, { method: 'POST', data: { state } });
};


export enum ChangePaymentTypeReason {
  clientForgotToAddInsurance = 'client_forgot_to_add_insurance',
  clientHasBackdatedInsurance = 'client_has_backdated_insurance',
  insuranceWasAutoArchivedAndNotUpdated = 'insurance_was_auto_archived_and_not_updated',
  issueWithCredentialing = 'issue_with_credentialing',
}

export const changeAppointmentPaymentTypeToInsurance = async (appointmentId: string, insuranceId: string, changeReason: ChangePaymentTypeReason) => {
  return restClient.request(`/api/v1/appointment/${appointmentId}/change_from_cash_to_insurance`, {
    method: 'POST',
    data: {
      insurance_id: insuranceId,
      payment_type_change_reason: changeReason
    }
  });
};

export const patchSession = async (sessionId: string, data: {
  primaryCptCodeId?: string;
  addonCptCodeIds?: string[];
}) => {
  return restClient.request<{ data: null }>(`/api/v1/session/${sessionId}`, { method: 'PATCH', data: snakecaseKeys(data) });
};

export const getSession = async (sessionId: string) => {
  return restClient.request<{data: SessionResource}>(`/api/v1/session/${sessionId}`);
};

export const getSessionVerificationLocation = async (sessionId: string) => {
  return restClient.request<{data: string | null}>(`/api/v1/session/${sessionId}/verification_location`);
};

export const getPrecedingSession = async (sessionId: string) => {
  return restClient.request<{data: SessionResource}>(`/api/v1/session/${sessionId}/get_preceding_session`);
};

export const getSessionClinicalNotes = async (sessionId: string) => {
  return restClient.request<{data: SessionResource}>(`/api/v1/session/${sessionId}/clinical_note`);
};

export const patchSessionClinicalNote = async (sessionId: string, data: PatchSessionInput) => {
  return restClient.request<{data: SessionResource}>(`/api/v1/session/${sessionId}/clinical_note`, { method: 'PATCH', data: snakecaseKeys(data) });
};


export const submitSession = async (sessionId: string, data: SubmitSessionInput) => {
  return restClient.request<{ data: SessionResource } | { error: string }>(`/api/v1/session/${sessionId}/submit`, { method: 'POST', data: snakecaseKeys(data) });
};

export const submitSessionForStudent = (sessionId: string, data: SubmitSessionInput) => {
  return restClient.request<{ data: SessionResource } | { error: string }>(`/api/v1/higher_ed/session/${sessionId}/submit`, { method: 'POST', data: snakecaseKeys(data) });
};

export const viewSessionNotes = async (sessionId: string) => {
  return restClient.request<{
    data: (
    | { type: AppointmentType.therapy; note: SessionNotesView }
    | { type: AppointmentType.psychiatry; note: PsychiatryNote }
    );
  }>(`/api/v1/session/${sessionId}/view_notes`);
};

export const getProviderCPTCodes = async () => {
  // will return cpt codes authorized to the provider, based on their licensure
  return restClient.request<{
    data: {
      cpt_codes: CPTCode[];
      filter_out_rules: {
        filter_out: string;
        if_selected: string;
      }[];
    };
  }>('/api/v1/cpt_codes/by_provider');
};
